import { UUID } from "@/types/common/ids";
import {
  DocumentItem,
  ListDocumentsQuery,
  PaginatedResultOfDocument,
  UpdateDocumentRequest,
} from "@/types/documents";
import { apiFetchRaw, apiFetch } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";

function appendQueryParam(sp: URLSearchParams, key: string, value: unknown) {
  if (value == null) return;
  if (Array.isArray(value)) {
    for (const val of value) {
      if (val == null) continue;
      sp.append(key, String(val));
    }
    return;
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    sp.set(key, String(value));
  }
}

function toQuery(q?: ListDocumentsQuery) {
  if (!q) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    appendQueryParam(sp, k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export class DocumentsService {
  static async list(query: ListDocumentsQuery = {}) {
    return apiFetch<PaginatedResultOfDocument>(
      `${GAIL_API}/.internal/documents${toQuery(query)}`,
      "GET",
      {
        alwaysReturn: true,
      }
    );
  }

  static async upload(files: File[]): Promise<DocumentItem[] | null> {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file, file.name);
    }

    try {
      const res = await apiFetchRaw(
        `${GAIL_API}/.internal/documents`,
        "POST",
        { body: formData }
      );

      if (res.ok) {
        const data = await res.json();
        return (data as { documents: DocumentItem[] }).documents;
      }
    } catch {
      // fall through
    }
    return null;
  }

  static async get(id: UUID) {
    return apiFetch<DocumentItem>(
      `${GAIL_API}/.internal/documents/${id}`,
      "GET",
      {
        alwaysReturn: true,
      }
    );
  }

  static async remove(id: UUID) {
    return apiFetch<DocumentItem>(
      `${GAIL_API}/.internal/documents/${id}`,
      "DELETE",
      {
        alwaysReturn: true,
      }
    );
  }

  static async download(
    id: UUID,
    suggestedFileName?: string | null
  ): Promise<boolean> {
    try {
      const response = await apiFetchRaw(
        `${GAIL_API}/.internal/documents/${id}/content`,
        "GET"
      );

      if (!response?.ok) {
        return false;
      }

      const blob = await response.blob();

      // Try to extract filename from Content-Disposition header
      const disposition = response.headers.get("content-disposition") || "";
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
        disposition
      );
      const headerFileName = decodeURIComponent(
        (match?.[1] || match?.[2] || "").trim()
      );

      const fileName =
        suggestedFileName?.trim() ||
        headerFileName ||
        // Fallback
        `document-${id}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (e) {
      console.error("Failed to download document", e);
      return false;
    }
  }

  /**
   * Fetches the binary content for a document and returns a temporary object URL
   * alongside basic metadata for previewing inside the app.
   * The caller is responsible for revoking the URL via `URL.revokeObjectURL`.
   */
  static async getContentUrl(
    id: UUID,
    desiredFileName?: string | null
  ): Promise<{
    url: string;
    contentType: string | null;
    fileName: string | null;
  } | null> {
    try {
      const response = await apiFetchRaw(
        `${GAIL_API}/.internal/documents/${id}/content`,
        "GET"
      );

      if (!response?.ok) {
        return null;
      }

      const blob = await response.blob();

      const disposition = response.headers.get("content-disposition") || "";
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
        disposition
      );
      const headerFileName = decodeURIComponent(
        (match?.[1] || match?.[2] || "").trim()
      );

      let contentType = response.headers.get("content-type") || "";

      // Prefer provided name, then header, then fallback
      let effectiveName =
        (desiredFileName ?? "").trim() || headerFileName || `document-${id}`;

      // Normalize filename based on content type to include proper extension
      const ensureExtension = (name: string, type: string | null) => {
        if (!type) return name;
        const lower = name.toLowerCase();
        if (type.startsWith("application/pdf") && !lower.endsWith(".pdf")) {
          return name + ".pdf";
        }
        if (type.startsWith("image/png") && !lower.endsWith(".png")) {
          return name + ".png";
        }
        if (type.startsWith("image/jpeg") && !/(\.jpe?g)$/i.test(lower)) {
          return name + ".jpg";
        }
        if (type.startsWith("image/webp") && !lower.endsWith(".webp")) {
          return name + ".webp";
        }
        return name;
      };

      effectiveName = ensureExtension(effectiveName, contentType);

      // Guess PDF when type missing but filename indicates PDF
      if (!contentType && /\.pdf$/i.test(effectiveName)) {
        contentType = "application/pdf";
      }

      // Create a blob URL with explicit content type for proper rendering
      const typedBlob = new Blob([blob], {
        type: contentType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(typedBlob);

      return { url, contentType, fileName: effectiveName };
    } catch (e) {
      console.error("Failed to fetch document content", e);
      return null;
    }
  }

  static async update(id: UUID, tags?: string[], fileName?: string) {
    const document = await this.get(id);
    if (!document) {
      return null;
    }

    if (tags) {
      document.tags = tags;
    }
    if (fileName) {
      document.fileName = fileName;
    }

    const updatedDocument: UpdateDocumentRequest = {
      tags: document.tags,
      fileName: document.fileName,
    };

    return apiFetch<DocumentItem>(
      `${GAIL_API}/.internal/documents/${id}`,
      "PUT",
      {
        alwaysReturn: true,
        body: updatedDocument,
      }
    );
  }

  // get all tags
  static async getTags() {
    return apiFetch<string[]>(
      `${GAIL_API}/.internal/documents/tags`,
      "GET"
    );
  }
}
